import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';


export const useDocuments = () => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [processingDocuments, setProcessingDocuments] = useState(new Set());
    const [error, setError] = useState(null);
    const pollingTimeouts = useRef(new Map());
    // Stable handle on the LATEST startPolling. Callbacks that need to start
    // polling read it through here instead of naming startPolling as a
    // dependency: startPolling depends on fetchDocuments, which depends on
    // currentWorkspaceId, so naming it directly would rebuild those callbacks on
    // every workspace switch (and, for uploadDocument, would be a
    // use-before-declaration since startPolling is defined further down).
    const startPollingRef = useRef(null);
    const toast = useToast();
    const { user } = useAuth();
    // Documents follow the workspace switcher, exactly like conversations.
    const { currentWorkspaceId } = useWorkspace();

    // Fetch documents for the CURRENT workspace.
    //
    // Before this, the request carried no workspace at all and the backend had
    // no filter, so every document on the instance came back. Once the workspace
    // migration copied each document into every workspace, that showed up as the
    // same document listed N times.
    const fetchDocuments = useCallback(async () => {
        if (!user) return [];
        // No workspace selected yet: ask for nothing rather than asking for
        // everything. An unscoped request would resurrect the duplicate listing.
        if (currentWorkspaceId == null) {
            setDocuments([]);
            return [];
        }

        try {
            const response = await fetchWithAuth(
                `/api/documents?workspace_id=${currentWorkspaceId}`, {}
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch documents');
            }
            
            const data = await response.json();
            setDocuments(data);
            return data;
        } catch (error) {
            console.error('Error fetching documents:', error);
            setError('Failed to load documents');
            return [];
        }
    }, [user, currentWorkspaceId]);

    // Rename document
    const renameDocument = useCallback(async (documentId, newFilename) => {
        try {
            if (!newFilename || !newFilename.trim()) {
                throw new Error('Filename cannot be empty');
            }
            const response = await fetchWithAuth(`/api/documents/${documentId}/rename`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: newFilename.trim() })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to rename document');
            }
            const data = await response.json();
            const updatedName = data.new_filename || newFilename.trim();

            setDocuments(prev =>
                prev.map(doc =>
                    doc.id === documentId ? { ...doc, filename: updatedName } : doc
                )
            );

            toast({
                title: 'Document Renamed',
                description: `New name: ${updatedName}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error renaming document:', error);
            toast({
                title: 'Rename Failed',
                description: error.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            throw error;
        }
    }, [toast]);


    // Upload document into the CURRENT workspace.
    //
    // workspace_id is required by the API: the server never infers where a
    // document lands (option A, same as creating a conversation). Sending the
    // wrong one would file an upload in another workspace, so it comes straight
    // from the switcher and is validated against membership server-side.
    const uploadDocument = useCallback(async (file, tier = 'standard') => {
        if (currentWorkspaceId == null) {
            const message = 'Select a workspace before uploading';
            setError(message);
            toast({
                title: 'Upload Failed',
                description: message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw new Error(message);
        }

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('ocr_tier', tier);
            formData.append('workspace_id', String(currentWorkspaceId));

            const response = await fetchWithAuth('/api/documents/upload', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Upload failed');
            }

            const newDocument = await response.json();
            
            // Add to documents list
            setDocuments(prev => [newDocument, ...prev]);
            
            // Start polling for this document. Via the ref so this callback does
            // not need startPolling as a dependency -- see the note at the end.
            if (newDocument.status === 'processing') {
                startPollingRef.current?.(newDocument.id);
            }

            toast({
                title: 'Upload Started',
                description: `${file.name} is being processed`,
                status: 'info',
                duration: 3000,
                isClosable: true,
            });

            return newDocument;

        } catch (error) {
            console.error('Error uploading document:', error);
            setError(error.message);
            toast({
                title: 'Upload Failed',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        } finally {
            setIsLoading(false);
        }
        // currentWorkspaceId MUST be here. Omitting it leaves this callback
        // closed over the workspace that was selected when it was created, so
        // the first upload after switching workspace would be filed into the
        // PREVIOUS one -- silently, with no error. Two eslint exhaustive-deps
        // warnings in !12 were exactly this bug, in the MR whose purpose was
        // preventing it.
        //
        // startPolling is reached via a ref (startPollingRef) rather than named
        // here on purpose: it is declared below and depends on fetchDocuments,
        // so naming it directly would either be a use-before-declaration or
        // force this callback to be rebuilt on every workspace switch.
    }, [toast, currentWorkspaceId]);

    // Start polling for a document
    const startPolling = useCallback((documentId) => {
        setProcessingDocuments(prev => new Set([...prev, documentId]));
        
        const poll = async () => {
            try {
                const response = await fetchWithAuth('/api/documents/check-processing', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const updatedDocumentIds = await response.json();
                    
                    if (updatedDocumentIds.includes(documentId)) {
                        // Document was updated, fetch fresh data
                        const freshDocuments = await fetchDocuments();
                        const updatedDoc = freshDocuments.find(doc => doc.id === documentId);
                        
                        if (updatedDoc && updatedDoc.status !== 'processing') {
                            // Processing finished
                            setProcessingDocuments(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(documentId);
                                return newSet;
                            });
                            
                            // Clear timeout
                            const timeoutId = pollingTimeouts.current.get(documentId);
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                pollingTimeouts.current.delete(documentId);
                            }

                            // Show completion toast
                            if (updatedDoc.status === 'completed') {
                                toast({
                                    title: 'Document Ready',
                                    description: `${updatedDoc.filename} processing completed`,
                                    status: 'success',
                                    duration: 4000,
                                    isClosable: true,
                                });
                            } else if (updatedDoc.status === 'failed') {
                                toast({
                                    title: 'Processing Failed',
                                    description: `${updatedDoc.filename}: ${updatedDoc.error_message}`,
                                    status: 'error',
                                    duration: 6000,
                                    isClosable: true,
                                });
                            }
                            return; // Stop polling
                        }
                    }
                }
            } catch (error) {
                console.error('Polling error:', error);
            }

            // Continue polling every 3 seconds
            const timeoutId = setTimeout(poll, 3000);
            pollingTimeouts.current.set(documentId, timeoutId);
        };

        // Start first poll immediately
        poll();

        // Set 5-minute timeout
        const maxTimeout = setTimeout(() => {
            setProcessingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });
            pollingTimeouts.current.delete(documentId);
            
            toast({
                title: 'Processing Timeout',
                description: 'Document processing took longer than expected',
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
        }, 5 * 60 * 1000); // 5 minutes

        pollingTimeouts.current.set(`${documentId}_max`, maxTimeout);
    }, [fetchDocuments, toast]);

    // Keep the ref pointing at the current startPolling, so callers that reach
    // it through startPollingRef never invoke a stale closure over an old
    // workspace's fetchDocuments.
    useEffect(() => {
        startPollingRef.current = startPolling;
    }, [startPolling]);

    // Get document detail
    const getDocument = useCallback(async (documentId) => {
        try {
            const response = await fetchWithAuth(`/api/documents/${documentId}`, {
            });
            if (!response.ok) {
                throw new Error('Document not found');
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching document:', error);
            throw error;
        }
    }, []);

    // Update document text
    const updateDocumentText = useCallback(async (documentId, text) => {
        try {
            const response = await fetchWithAuth(`/api/documents/${documentId}/text`, {
                method: 'PUT',
                body: JSON.stringify({ text })
            });

            if (!response.ok) {
                throw new Error('Failed to update document');
            }

            const result = await response.json();
            
            toast({
                title: 'Document Updated',
                description: 'Changes saved successfully',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            return result;
        } catch (error) {
            console.error('Error updating document:', error);
            toast({
                title: 'Save Failed',
                description: error.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            throw error;
        }
    }, [toast]);

    // Delete document
    const deleteDocument = useCallback(async (documentId) => {
        try {
            const response = await fetchWithAuth(`/api/documents/${documentId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete document');
            }

            // Remove from local state
            setDocuments(prev => prev.filter(doc => doc.id !== documentId));
            
            // Stop polling if active
            setProcessingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });

            // Clear timeouts
            const timeoutId = pollingTimeouts.current.get(documentId);
            const maxTimeoutId = pollingTimeouts.current.get(`${documentId}_max`);
            if (timeoutId) clearTimeout(timeoutId);
            if (maxTimeoutId) clearTimeout(maxTimeoutId);
            pollingTimeouts.current.delete(documentId);
            pollingTimeouts.current.delete(`${documentId}_max`);

            toast({
                title: 'Document Deleted',
                description: 'Document removed successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Error deleting document:', error);
            toast({
                title: 'Delete Failed',
                description: error.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            throw error;
        }
    }, [toast]);

    // Retry document processing
    const retryDocument = useCallback(async (documentId) => {
        try {
            const response = await fetchWithAuth(`/api/documents/${documentId}/retry`, {
                method: 'POST',
            });

            if (!response.ok) {
                throw new Error('Failed to retry document processing');
            }

            const updatedDocument = await response.json();
            
            // Update local state
            setDocuments(prev => 
                prev.map(doc => 
                    doc.id === documentId ? updatedDocument : doc
                )
            );

            // Start polling again
            if (updatedDocument.status === 'processing') {
                startPolling(documentId);
            }

            toast({
                title: 'Retry Started',
                description: 'Document processing restarted',
                status: 'info',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Error retrying document:', error);
            toast({
                title: 'Retry Failed',
                description: error.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    }, [startPolling, toast]);

    // Initialise, and RE-initialise whenever the workspace changes.
    //
    // This effect's dependencies change on every workspace switch: since !26
    // fetchDocuments depends on currentWorkspaceId, and startPolling depends on
    // fetchDocuments. Re-running is CORRECT -- the newly selected workspace's
    // processing documents do need polling.
    //
    // What was not correct was the cleanup. It was labelled "cleanup on unmount"
    // but React runs an effect's cleanup before EVERY re-run, not only on
    // unmount. The old code relied on the unmount reading, so nothing cancelled
    // the previous workspace's timers: each switch left another 3-second polling
    // loop running against /api/documents/check-processing, forever. Timers grew
    // linearly with the number of switches (verified: 4 -> 8 -> 12 -> 20 for
    // 1/2/3/5 switches; flat at 4 once cleaned up).
    //
    // `cancelled` additionally stops the in-flight initialise from calling
    // startPolling after the workspace has already changed underneath it --
    // otherwise a slow fetch could revive polling for documents the user has
    // navigated away from.
    useEffect(() => {
        let cancelled = false;
        // Copy the ref into a local: the ref's contents will have changed by the
        // time cleanup runs, and the timers this run creates are the ones this
        // run must cancel.
        const timeouts = pollingTimeouts.current;

        const initializeDocuments = async () => {
            const docs = await fetchDocuments();
            if (cancelled) return;

            docs.forEach(doc => {
                if (doc.status === 'processing') {
                    startPolling(doc.id);
                }
            });
        };

        initializeDocuments();

        // Runs before every re-run AND on unmount.
        return () => {
            cancelled = true;
            timeouts.forEach(timeoutId => clearTimeout(timeoutId));
            timeouts.clear();
        };
    }, [fetchDocuments, startPolling]);

    return {
        documents,
        isLoading,
        processingDocuments,
        error,
        uploadDocument,
        getDocument,
        updateDocumentText,
        deleteDocument,
        retryDocument,
        fetchDocuments,
        renameDocument
    };
};

